import { planAtLeast } from "@/lib/plans";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Lock, Loader2, X, History } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { LazyAuthModal as AuthModal } from "@/components/auth/LazyAuthModal";
import { Button } from "@/components/ui/button";
import { aiSearch, getAiSearchStatus } from "@/lib/ai-search.functions";
import { track } from "@/lib/analytics-client";

import { cacheAiSearch, getCachedAiSearch } from "@/lib/ai-search-cache";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizedPath } from "@/lib/i18n/slug-map";

export type AiFilters = { q?: string; kategori?: string; maxPris?: number; minPris?: number };

export function AiSearchPanel({
  term,
  onApply,
}: {
  term: string;
  onApply: (filters: AiFilters, explanation: string) => void;
}) {
  const { user } = useAuth();
  const { lang } = useLocale();
  const t = lang === "es" ? { network: "Sin conexión con el servicio de IA. Comprueba tu conexión e inténtalo de nuevo.", failed: "La búsqueda con IA no está disponible ahora. Inténtalo de nuevo más tarde.", refine: "Afinar con IA", example: "p. ej., «bicicleta eléctrica barata en Málaga por menos de 500 €»", login: "Iniciar sesión", previous: "Respuesta anterior", interpret: "Interpretar", left: "restantes", close: "Cerrar búsqueda con IA", reused: "Hemos reutilizado tu respuesta anterior.", used: "Has utilizado tus búsquedas con IA de hoy.", plans: "Ver Plus y Pro", retry: "Intentar de nuevo" }
    : lang === "en" ? { network: "Could not reach the AI service. Check your connection and try again.", failed: "AI search is unavailable right now. Try again shortly.", refine: "Refine with AI", example: "e.g. ‘cheap electric bike in London under £500’", login: "Sign in", previous: "Previous result", interpret: "Interpret", left: "left", close: "Close AI search", reused: "Reused your previous AI result.", used: "You have used today's AI searches.", plans: "View Plus and Pro", retry: "Try again" }
      : { network: "Ingen kontakt med AI-tjänsten. Kontrollera din uppkoppling och prova igen.", failed: "AI-sökningen kunde inte köras just nu. Prova igen om en stund.", refine: "Förfina med AI", example: "t.ex. ”billig elcykel i Malmö under 5000 kr”", login: "Logga in", previous: "Tidigare svar", interpret: "Tolka", left: "kvar", close: "Stäng AI-sökning", reused: "Återanvände ditt tidigare AI-svar.", used: "Du har använt dina AI-sökningar idag.", plans: "Se Plus och Pro", retry: "Försök igen" };
  const [authOpen, setAuthOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<string | null>(null);
  const [reused, setReused] = useState(false);

  const fetchStatus = useServerFn(getAiSearchStatus);
  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ["ai-search-status", user?.id],
    queryFn: () => fetchStatus({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const runSearch = useServerFn(aiSearch);
  const mutation = useMutation({
    mutationFn: (q: string) => {
      track("search", { meta: { kind: "ai" } });
      return runSearch({ data: { query: q } });
    },

    onSuccess: (res, q) => {
      if (!res.ok) {
        setQuota(res.message);
        void refetchStatus();
        return;
      }
      setQuota(null);
      cacheAiSearch(q, { filters: res.filters, explanation: res.explanation });
      void refetchStatus();
      onApply(res.filters, res.explanation);
    },
    onError: (e) =>
      setError(
        e instanceof Error && /nätverk|fetch|network/i.test(e.message)
           ? t.network
           : t.failed,
      ),
  });

  const cached = user ? getCachedAiSearch(term) : null;
  const outOfQuota = !!status && status.remaining <= 0;

  const visible = term.trim().length >= 3 && !dismissed;
  if (!visible) return null;

  const run = () => {
    setError(null);
    setQuota(null);
    const hit = getCachedAiSearch(term);
    if (hit) {
      setReused(true);
      onApply(hit.filters, hit.explanation);
      return;
    }
    setReused(false);
    mutation.mutate(term.trim());
  };

  return (
    <>
      <div className="mt-2 rounded-md border border-primary/30 bg-ocean-100/50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
           <p className="text-xs font-semibold text-foreground">{t.refine}</p>
          <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
             {t.example}
          </p>

          {!user ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setAuthOpen(true)}
            >
               <Lock className="h-3.5 w-3.5" /> {t.login}
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                disabled={mutation.isPending || (outOfQuota && !cached)}
                onClick={run}
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : cached ? (
                  <History className="h-3.5 w-3.5" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                 {cached ? t.previous : t.interpret}
              </Button>
              {status && (
                <span
                  className="shrink-0 text-[11px] text-muted-foreground"
                  title={`Plan: ${status.plan}`}
                >
                   {status.remaining}/{status.limit} {t.left}
                </span>
              )}
            </>
          )}

          <button
            type="button"
             aria-label={t.close}
            onClick={() => setDismissed(true)}
            className="shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {reused && (
           <p className="mt-2 text-xs text-muted-foreground">{t.reused}</p>
        )}

        {(quota || (outOfQuota && !cached)) && (
          <p className="mt-2 text-xs text-foreground">
             {quota ?? `${t.used} (${status?.limit})`}{" "}
            {!planAtLeast(status?.plan, "pro") && (
              <Link
                 to={localizedPath("/priser", lang)}
                className="font-semibold text-primary underline underline-offset-2"
              >
                 {t.plans}
              </Link>
            )}
          </p>
        )}
        {error && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-destructive">
            {error}
            <button
              type="button"
              onClick={run}
              className="font-semibold underline underline-offset-2"
            >
               {t.retry}
            </button>
          </p>
        )}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
